/**
 * TextHighlighter Component
 * Finds extracted text in the PDF DOM and highlights it using CSS.
 * This runs AFTER react-pdf renders the text layer.
 * 
 * Features:
 * - Highlights all extracted components on the current page
 * - Color-coded by verification status: Green (verified), Red (incorrect), Amber (to review)
 * - Applies special styling to selected items
 * - Auto-scrolls to selected item when it changes
 * - Supports click handlers for item selection
 */

import React, { useEffect, useCallback, useRef, useState } from 'react';
import { BOMItem } from '../../types/bom.types';

// Debug logging flag - set to true to see highlighting debug info in console
const DEBUG_HIGHLIGHTING = true;

const debugLog = (...args: any[]) => {
  if (DEBUG_HIGHLIGHTING) {
    console.log('[TextHighlighter]', ...args);
  }
};

// Verification status type
type VerificationStatus = 'verified' | 'incorrect' | 'unverified';

interface TextHighlighterProps {
  items: BOMItem[];
  pageNumber: number;
  selectedItem?: BOMItem | null;
  onItemClick?: (item: BOMItem) => void;
  onHighlightFound?: (element: HTMLElement | null, item: BOMItem) => void;
  /** Enable status-based coloring (green=verified, red=incorrect, amber=to review) */
  showVerificationStatus?: boolean;
}

// Status-based colors (primary visual indicator) - very light backgrounds for readability
const STATUS_COLORS: Record<VerificationStatus, { bg: string; border: string; label: string }> = {
  verified: { 
    bg: 'rgba(76, 175, 80, 0.12)',      // Very light green tint
    border: '#4caf50',                   // Green underline
    label: 'Validated'
  },
  incorrect: { 
    bg: 'rgba(244, 67, 54, 0.12)',      // Very light red tint
    border: '#f44336',                   // Red underline
    label: 'Not Valid'
  },
  unverified: { 
    bg: 'rgba(255, 193, 7, 0.12)',      // Very light amber tint
    border: '#ffc107',                   // Amber underline
    label: 'To Review'
  },
};

// Color mapping for component types (fallback when status coloring is off)
const TYPE_COLORS: Record<string, string> = {
  MCB: 'rgba(33, 150, 243, 0.4)',      // Blue
  CONTACTOR: 'rgba(76, 175, 80, 0.4)', // Green
  RELAY: 'rgba(255, 152, 0, 0.4)',     // Orange
  TERMINAL: 'rgba(156, 39, 176, 0.4)', // Purple
  PLC: 'rgba(233, 30, 99, 0.4)',       // Pink
  MOTOR: 'rgba(0, 188, 212, 0.4)',     // Cyan
  FUSE: 'rgba(255, 87, 34, 0.4)',      // Deep Orange
  TRANSFORMER: 'rgba(121, 85, 72, 0.4)', // Brown
  POWER_SUPPLY: 'rgba(96, 125, 139, 0.4)', // Blue Grey
  GENERATOR: 'rgba(255, 193, 7, 0.4)', // Amber
  FAN: 'rgba(0, 150, 136, 0.4)',       // Teal
  HMI: 'rgba(103, 58, 183, 0.4)',      // Deep Purple
  IO_MODULE: 'rgba(63, 81, 181, 0.4)', // Indigo
  OTHER: 'rgba(158, 158, 158, 0.4)',   // Grey
};

const getHighlightColor = (componentType?: string, confidence?: number) => {
  const baseColor = TYPE_COLORS[componentType?.toUpperCase() || 'OTHER'] || TYPE_COLORS.OTHER;
  
  // Adjust opacity based on confidence
  if (confidence !== undefined && confidence < 0.5) {
    return baseColor.replace('0.4', '0.2');
  }
  return baseColor;
};

// Get status-based styling
const getStatusStyle = (status: VerificationStatus | undefined) => {
  const statusKey = status || 'unverified';
  return STATUS_COLORS[statusKey] || STATUS_COLORS.unverified;
};

// Helper function to highlight a span element
const highlightSpan = (
  span: HTMLElement,
  item: BOMItem,
  showVerificationStatus: boolean,
  selectedItem: BOMItem | null | undefined,
  onItemClick: ((item: BOMItem) => void) | undefined,
  clickHandlersRef: React.MutableRefObject<Map<HTMLElement, (e: Event) => void>>
) => {
  const reference = item.reference || '';
  const verificationStatus = item.verification_status as VerificationStatus | undefined;
  
  // Check if already highlighted
  if (span.classList.contains('bom-highlight')) return;
  
  debugLog(`✅ HIGHLIGHTING: "${reference}" in span with text: "${span.textContent?.substring(0, 50)}..."`);
  
  span.classList.add('bom-highlight');
  span.setAttribute('data-bom-ref', reference);
  span.setAttribute('data-bom-type', item.component_type || 'OTHER');
  span.setAttribute('data-bom-status', verificationStatus || 'unverified');
  
  // Apply status-based or type-based coloring
  span.style.fontWeight = 'bold';
  span.style.textShadow = '0 0 1px currentColor';
  
  if (showVerificationStatus) {
    const statusStyle = getStatusStyle(verificationStatus);
    span.style.boxShadow = `0 5px 0 0 ${statusStyle.border}`;
    span.style.backgroundColor = statusStyle.bg;
    span.classList.add(`bom-status-${verificationStatus || 'unverified'}`);
  } else {
    const bgColor = getHighlightColor(item.component_type, item.confidence);
    const solidColor = bgColor.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+).*\)/, 'rgb($1, $2, $3)');
    span.style.boxShadow = `0 5px 0 0 ${solidColor}`;
    span.style.backgroundColor = bgColor;
  }
  
  span.style.cursor = 'pointer';
  span.style.borderRadius = '2px';
  
  // Set tooltip
  const desc = item.description || 'No description';
  const type = item.component_type || 'Unknown type';
  const conf = item.confidence !== undefined ? `${(item.confidence * 100).toFixed(0)}%` : 'N/A';
  const statusLabel = showVerificationStatus ? `\nStatus: ${getStatusStyle(verificationStatus).label}` : '';
  span.title = `${reference}: ${desc}\nType: ${type}\nConfidence: ${conf}${statusLabel}`;
  
  // Apply selected styling
  if (selectedItem && selectedItem.reference === reference) {
    span.classList.add('bom-highlight-selected');
    const statusStyle = showVerificationStatus ? getStatusStyle(verificationStatus) : null;
    const underline = statusStyle ? `0 5px 0 0 ${statusStyle.border}` : '';
    span.style.boxShadow = `${underline}${underline ? ', ' : ''}0 0 0 2px #1976d2, 0 0 8px 4px rgba(25, 118, 210, 0.4)`;
  }
  
  // Add click handler
  if (onItemClick) {
    const handler = (e: Event) => {
      e.stopPropagation();
      onItemClick(item);
    };
    span.addEventListener('click', handler);
    clickHandlersRef.current.set(span, handler);
  }
};

const TextHighlighter: React.FC<TextHighlighterProps> = ({
  items,
  pageNumber,
  selectedItem,
  onItemClick,
  onHighlightFound,
  showVerificationStatus = true, // Default to showing status colors
}) => {
  const lastSelectedRef = useRef<string | null>(null);
  const selectedElementRef = useRef<HTMLElement | null>(null);
  const isHighlightingRef = useRef<boolean>(false);
  const clickHandlersRef = useRef<Map<HTMLElement, (e: Event) => void>>(new Map());
  const hostRef = useRef<HTMLSpanElement>(null);
  const [hostDoc, setHostDoc] = useState<Document>(document);

  // The PDF can be portaled into a popup. That window has its own document,
  // so the highlighter has to follow the document the page was moved into.
  useEffect(() => {
    const next = hostRef.current?.ownerDocument || document;
    if (next !== hostDoc) setHostDoc(next);
  });

  const pageDocument = useCallback(() => hostDoc, [hostDoc]);

  const highlightText = useCallback(() => {
    // Prevent infinite loop from MutationObserver
    if (isHighlightingRef.current) return;
    isHighlightingRef.current = true;

    try {
      const textLayer = pageDocument().querySelector(
        `.react-pdf__Page[data-page-number="${pageNumber}"] .react-pdf__Page__textContent`
      );
      
      if (!textLayer) {
        isHighlightingRef.current = false;
        return;
      }

      // Get all text spans
      const spans = Array.from(textLayer.querySelectorAll('span')) as HTMLElement[];
      
      // Filter items for this page
      const pageItems = items.filter(item => {
        if (item.page === pageNumber) return true;
        if (item.pages && item.pages.includes(pageNumber)) return true;
        return false;
      });

      // Clear previous highlights and remove old click handlers
      spans.forEach(span => {
        // Remove old click handler if exists
        const oldHandler = clickHandlersRef.current.get(span);
        if (oldHandler) {
          span.removeEventListener('click', oldHandler);
          clickHandlersRef.current.delete(span);
        }
        
        span.classList.remove('bom-highlight', 'bom-highlight-selected', 'bom-highlight-flash', 
          'bom-status-verified', 'bom-status-incorrect', 'bom-status-unverified');
        span.removeAttribute('data-bom-ref');
        span.removeAttribute('data-bom-type');
        span.removeAttribute('data-bom-status');
        span.style.backgroundColor = '';
        span.style.cursor = '';
        span.style.outline = '';
        span.style.outlineOffset = '';
        span.style.boxShadow = '';
        span.style.zIndex = '';
        span.style.position = '';
        span.style.border = '';
        span.style.borderBottom = '';
        span.title = '';
      });

      let selectedElement: HTMLElement | null = null;

      // Apply highlights for each item
      // Sort items by reference length (longest first) to prevent shorter matches 
      // from being highlighted when a longer exact match exists
      const sortedItems = [...pageItems].sort((a, b) => 
        (b.reference?.length || 0) - (a.reference?.length || 0)
      );
      
      // Track which spans have been claimed by which reference (to prevent overlapping)
      const claimedSpans = new Map<HTMLElement, string>();
      
      debugLog(`Processing ${sortedItems.length} items for page ${pageNumber}`);
      
      sortedItems.forEach(item => {
        const reference = item.reference;
        if (!reference) return;

        // Normalize reference for comparison (trim and handle case)
        const refNormalized = reference.trim();
        const refUpper = refNormalized.toUpperCase();
        debugLog(`Looking for: "${reference}" (normalized: "${refNormalized}")`);
        
        // For multi-word references, we need to check if consecutive spans form the reference
        const refWords = refNormalized.split(/\s+/);
        const isMultiWord = refWords.length > 1;

        spans.forEach((span, spanIdx) => {
          // Skip if this span is already claimed by another (longer) reference
          const claimedBy = claimedSpans.get(span);
          if (claimedBy) {
            debugLog(`Span already claimed by "${claimedBy}", skipping for "${reference}"`);
            return;
          }
          
          const text = span.textContent || '';
          const textTrimmed = text.trim();
          const textUpper = textTrimmed.toUpperCase();
          
          // Check if this span contains the reference
          // PRIORITY: Exact matches first, then word-boundary matches
          let containsRef = false;
          let isExactMatch = false;
          
          // Exact match (case-insensitive) - highest priority
          if (textTrimmed === refNormalized || textUpper === refUpper) {
            containsRef = true;
            isExactMatch = true;
          }
          // Word-boundary match: reference must be at word boundary, not a substring of another reference
          // e.g., "0KM" should NOT match in "0KM0" or "0KM00"
          else if (textUpper.includes(refUpper)) {
            // Check if the match is at a word boundary (not followed by more alphanumeric chars)
            const refIndex = textUpper.indexOf(refUpper);
            const charAfter = textUpper[refIndex + refUpper.length];
            const charBefore = refIndex > 0 ? textUpper[refIndex - 1] : undefined;
            
            // Only match if:
            // 1. Reference is at the end of the text, OR
            // 2. Character after is not alphanumeric (word boundary)
            // 3. Character before is not alphanumeric (word boundary at start)
            const isWordBoundaryEnd = !charAfter || !/[A-Z0-9]/.test(charAfter);
            const isWordBoundaryStart = !charBefore || !/[A-Z0-9]/.test(charBefore);
            
            if (isWordBoundaryEnd && isWordBoundaryStart) {
              containsRef = true;
            } else {
              debugLog(`Skipping partial match: "${refNormalized}" in "${textTrimmed}" - not at word boundary`);
            }
          }
          // Handle references with dash prefix (e.g., -P.E. vs P.E.)
          else if (textUpper === `-${refUpper}` || textUpper.includes(`-${refUpper}`)) {
            containsRef = true;
            isExactMatch = textUpper === `-${refUpper}`;
          }
          // Handle period-containing references like P.E., F3.4A, C2.4
          else if (refNormalized.includes('.')) {
            // Try without dots - exact match only
            const refNoDots = refNormalized.replace(/\./g, '');
            if (textTrimmed === refNoDots || textUpper === refNoDots.toUpperCase()) {
              containsRef = true;
              isExactMatch = true;
            }
            // Try with spaces around dots (PDF sometimes adds spaces) - exact match only
            const refWithSpaces = refNormalized.replace(/\./g, ' . ').replace(/\s+/g, ' ').trim();
            if (textUpper === refWithSpaces.toUpperCase()) {
              containsRef = true;
              isExactMatch = true;
            }
            // Check if text is the first part of a multi-part reference (e.g., "F3" part of "F3.4A")
            // ONLY if we can complete the full reference from consecutive spans
            if (!containsRef) {
              const refParts = refNormalized.split('.');
              if (refParts.length > 1) {
                const firstPart = refParts[0].toUpperCase();
                if (textUpper === firstPart) {
                  // Check if next spans complete the reference
                  let fullMatch = textTrimmed;
                  let nextIdx = spanIdx + 1;
                  const spansToHighlight: number[] = [];
                  
                  while (nextIdx < spans.length && fullMatch.length < refNormalized.length + 3) {
                    const nextText = (spans[nextIdx].textContent || '').trim();
                    if (nextText) {
                      fullMatch += nextText;
                      spansToHighlight.push(nextIdx);
                      
                      // Check for exact or near-exact match (ignoring spaces)
                      const fullMatchNormalized = fullMatch.toUpperCase().replace(/\s/g, '');
                      const refNormalized2 = refUpper.replace(/\s/g, '');
                      if (fullMatchNormalized === refNormalized2) {
                        containsRef = true;
                        // Highlight the additional spans
                        for (const idx of spansToHighlight) {
                          const subSpan = spans[idx] as HTMLElement;
                          if (subSpan.textContent?.trim()) {
                            highlightSpan(subSpan, item, showVerificationStatus, selectedItem, onItemClick, clickHandlersRef);
                            claimedSpans.set(subSpan, reference);
                          }
                        }
                        break;
                      }
                    }
                    nextIdx++;
                  }
                }
              }
            }
          }
          // Handle alphanumeric references that might be split across spans (e.g., "12W9A" might be "12W9" + "A")
          // ONLY match if the current span is the START of the reference and consecutive spans complete it
          else if (/^[A-Z0-9]+$/i.test(refNormalized) && textUpper.length < refUpper.length) {
            // Text must be a prefix of the reference (not just start with same chars)
            if (refUpper.startsWith(textUpper)) {
              // Try to build up the full reference from consecutive spans
              let fullMatch = textTrimmed;
              let nextIdx = spanIdx + 1;
              let foundFullMatch = false;
              const spansToHighlight: number[] = [];
              
              while (nextIdx < spans.length && fullMatch.length < refNormalized.length + 2) {
                const nextText = (spans[nextIdx].textContent || '').trim();
                if (nextText) {
                  fullMatch += nextText;
                  spansToHighlight.push(nextIdx);
                  
                  // Check for exact match, not just includes
                  if (fullMatch.toUpperCase() === refUpper) {
                    foundFullMatch = true;
                    containsRef = true;
                    // Highlight the additional spans
                    for (const idx of spansToHighlight) {
                      const subSpan = spans[idx] as HTMLElement;
                      if (subSpan.textContent?.trim()) {
                        highlightSpan(subSpan, item, showVerificationStatus, selectedItem, onItemClick, clickHandlersRef);
                        claimedSpans.set(subSpan, reference);
                      }
                    }
                    break;
                  }
                }
                nextIdx++;
              }
              
              // Only proceed if we found a complete match
              if (!foundFullMatch) {
                containsRef = false;
              }
            }
          }
          
          // Multi-word reference matching (e.g., "Interface PLC", "Hilscher Gateway Device")
          if (!containsRef && isMultiWord) {
            // Check if this span starts a multi-word sequence
            const firstWord = refWords[0].toUpperCase();
            if (textUpper === firstWord || textUpper.includes(firstWord)) {
              // Try to match subsequent words in following spans
              let matchedWords = 1;
              let currentSpanIdx = spanIdx + 1;
              
              while (matchedWords < refWords.length && currentSpanIdx < spans.length) {
                const nextSpan = spans[currentSpanIdx];
                const nextText = (nextSpan.textContent || '').trim().toUpperCase();
                const expectedWord = refWords[matchedWords].toUpperCase();
                
                // Skip empty spans
                if (!nextText) {
                  currentSpanIdx++;
                  continue;
                }
                
                if (nextText === expectedWord || nextText.includes(expectedWord)) {
                  matchedWords++;
                  currentSpanIdx++;
                } else {
                  break;
                }
              }
              
              // If all words matched, highlight all spans in the sequence
              if (matchedWords === refWords.length) {
                containsRef = true;
                // Mark the subsequent spans as well
                for (let i = spanIdx + 1; i < currentSpanIdx; i++) {
                  const subSpan = spans[i] as HTMLElement;
                  if (subSpan.textContent?.trim()) {
                    highlightSpan(subSpan, item, showVerificationStatus, selectedItem, onItemClick, clickHandlersRef);
                  }
                }
              }
            }
          }

          if (containsRef) {
            debugLog(`  ✓ MATCH FOUND for "${reference}" in span: "${textTrimmed.substring(0, 30)}..." (exact: ${isExactMatch})`);
            // Claim this span for this reference to prevent shorter matches from overlapping
            claimedSpans.set(span, reference);
            
            // Use helper function to highlight the span
            highlightSpan(span, item, showVerificationStatus, selectedItem, onItemClick, clickHandlersRef);
            
            // Track selected element
            if (selectedItem && selectedItem.reference === reference) {
              selectedElement = span;
            }
          }
        });
      });

      // Store reference to selected element
      selectedElementRef.current = selectedElement;

      // Notify parent if selected element was found
      if (selectedItem && onHighlightFound) {
        onHighlightFound(selectedElement, selectedItem);
      }
    } finally {
      // Reset flag after a small delay to allow DOM to settle
      setTimeout(() => {
        isHighlightingRef.current = false;
      }, 50);
    }
  }, [items, pageNumber, selectedItem, onItemClick, onHighlightFound, showVerificationStatus, pageDocument]);

  // Auto-scroll to selected item when it changes
  useEffect(() => {
    if (selectedItem && selectedItem.reference && selectedItem.reference !== lastSelectedRef.current) {
      lastSelectedRef.current = selectedItem.reference || null;
      
      // Wait for highlighting to complete, then scroll
      const scrollTimer = setTimeout(() => {
        const element = selectedElementRef.current || 
          pageDocument().querySelector(`.bom-highlight-selected[data-bom-ref="${selectedItem.reference}"]`);
        
        if (element) {
          // Scroll element into view with smooth animation
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });

          // Add a flash animation to draw attention
          element.classList.add('bom-highlight-flash');
          setTimeout(() => {
            element.classList.remove('bom-highlight-flash');
          }, 1500);
        }
      }, 150);

      return () => clearTimeout(scrollTimer);
    }
  }, [selectedItem, pageDocument]);

  // Run highlighting when dependencies change. The text layer often arrives
  // after the first attempt, so retry until it is there.
  useEffect(() => {
    const timers = [100, 400, 1000].map(delay => window.setTimeout(highlightText, delay));
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, [highlightText]);

  // Re-run when text layer might have been re-rendered
  useEffect(() => {
    const observer = new MutationObserver(() => {
      highlightText();
    });

    const pdfPage = pageDocument().querySelector(
      `.react-pdf__Page[data-page-number="${pageNumber}"]`
    );

    if (pdfPage) {
      observer.observe(pdfPage, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, [pageNumber, highlightText, pageDocument]);

  // Inject CSS styles on mount, into the document that actually holds the PDF
  useEffect(() => {
    const doc = pageDocument();
    const styleId = 'bom-highlight-styles';
    if (!doc.getElementById(styleId)) {
      const style = doc.createElement('style');
      style.id = styleId;
      style.textContent = highlightStyles;
      doc.head.appendChild(style);
    }
  }, [pageDocument]);

  return <span ref={hostRef} style={{ display: 'none' }} />;
};

export default TextHighlighter;

// CSS styles to inject (can be added to index.css or a separate stylesheet)
export const highlightStyles = `
  .bom-highlight {
    cursor: pointer;
  }
  
  .bom-highlight:hover {
    filter: brightness(1.1);
  }
  
  .bom-highlight-selected {
    animation: pulse-glow 1.5s ease-in-out infinite;
  }
  
  .bom-highlight-flash {
    animation: flash-attention 0.5s ease-in-out 3;
  }
  
  /* Status-based highlight styles - bold text with colored underline below text */
  .bom-status-verified {
    font-weight: bold !important;
    background-color: rgba(76, 175, 80, 0.12) !important;
    box-shadow: 0 5px 0 0 #4caf50 !important;
  }
  
  .bom-status-verified:hover {
    background-color: rgba(76, 175, 80, 0.2) !important;
  }
  
  .bom-status-incorrect {
    font-weight: bold !important;
    background-color: rgba(244, 67, 54, 0.12) !important;
    box-shadow: 0 5px 0 0 #f44336 !important;
  }
  
  .bom-status-incorrect:hover {
    background-color: rgba(244, 67, 54, 0.2) !important;
  }
  
  .bom-status-unverified {
    font-weight: bold !important;
    background-color: rgba(255, 193, 7, 0.12) !important;
    box-shadow: 0 5px 0 0 #ffc107 !important;
  }
  
  .bom-status-unverified:hover {
    background-color: rgba(255, 193, 7, 0.2) !important;
  }
  
  @keyframes pulse-glow {
    0%, 100% {
      box-shadow: 0 0 12px 4px rgba(25, 118, 210, 0.5);
    }
    50% {
      box-shadow: 0 0 20px 8px rgba(25, 118, 210, 0.8);
    }
  }
  
  @keyframes flash-attention {
    0%, 100% {
      transform: scale(1);
      filter: brightness(1);
    }
    50% {
      transform: scale(1.15);
      filter: brightness(1.3);
    }
  }
`;

